// =============================================================================
// Dashboard page — the internal current-state + forecast view (DashboardNow).
//
// Formerly split into "Now" / "Plan & Forecast" sub-tabs; consolidated to a single
// scrolling view. The forward-looking content that earned its place (the full-horizon
// performance-against-plan burn-up, the T-Minus stage progression, the programme target)
// was folded into DashboardNow; the redundant Plan-tab widgets (duplicate forecast/run-rate
// cards, hardcoded wave-milestones table, pipeline-health, static process reference) were
// dropped. History: the old standalone Plan.tsx is at commit ad9b648; the sub-tab shell
// preceded this consolidation.
// =============================================================================
import { Printer } from 'lucide-react'
import { api } from '../lib/api'
import { useAsync } from '../hooks/useAsync'
import { Card, Button, Skeleton, ErrorState, SectionTitle } from '../components/ui'
import { fmtNum, fmtDate } from '../lib/format'
import { DashboardNow } from './dashboard/DashboardNow'
import type { Insights, Meta } from '../lib/types'

export function DashboardPage({ meta }: { meta: Meta }) {
  const { data: insights, loading, error, reload } = useAsync<Insights>(() => api.insights(), [])

  if (loading && !insights) return <div className="space-y-4"><SectionTitle sub="Loading…">Dashboard</SectionTitle><div className="grid grid-cols-3 gap-px rounded-card border border-line bg-line lg:grid-cols-6">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-24" />)}</div></div>
  if (error && !insights) return <Card className="p-4"><ErrorState message={error} onRetry={reload} /></Card>
  if (!insights) return null

  return (
    <div className="space-y-5">
      <SectionTitle sub={`Internal view · ${fmtNum(insights.headline.total_in_scope)} sites in scope · Programme target ${fmtDate(insights.headline.target_date)}`}
        right={<Button size="sm" onClick={() => window.print()}><Printer className="h-3.5 w-3.5" /> Print / PDF</Button>}>
        Dashboard
      </SectionTitle>

      <DashboardNow insights={insights} meta={meta} />
    </div>
  )
}
